import { api } from '@/lib/api';
import type { LeadOrigin } from '@valiosascrm/shared';

export type { LeadOrigin } from '@valiosascrm/shared';

export interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  estimatedValue: number | null;
  origin: LeadOrigin;
  tags: string[];
  columnId: string;
  position: number | null;
  archivedAt: string | null;
  convertedToPatientId: string | null;
  patientId: string | null;
  createdAt: string;
  updatedAt: string;
  column?: KanbanColumn;
  interactions?: LeadInteraction[];
  conversationUnreadCount?: number;
}

export interface KanbanColumn {
  id: string;
  name: string;
  color: string;
  order: number;
  isDefault: boolean;
  leadCount?: number;
}

export interface LeadInteraction {
  id: string;
  leadId: string;
  type: 'message_received' | 'message_sent' | 'note' | 'call' | 'column_change';
  content: string | null;
  metadata: Record<string, any> | null;
  createdById: string | null;
  createdAt: string;
}

export interface LeadListResult {
  data: Lead[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LeadQueryParams {
  columnId?: string;
  origin?: string;
  tags?: string[];
  search?: string;
  /** 45.9 (G-TYPE): filtro server-side — lead = sem vínculo; patient = card da base OU convertido. */
  type?: 'lead' | 'patient';
  includeArchived?: boolean;
  minValue?: number;
  maxValue?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface CreateLeadData {
  name: string;
  phone: string;
  email?: string;
  estimatedValue?: number;
  columnId?: string;
  origin?: string;
  tags?: string[];
  notes?: string;
}

export interface UpdateLeadData extends Partial<Omit<CreateLeadData, 'notes'>> {}

export interface MoveLeadData {
  columnId: string;
  position?: number;
}

export interface CreateInteractionData {
  type: 'note' | 'call';
  content: string;
}

export interface SendMessageData {
  message: string;
}

export interface SendMessageResult {
  success: boolean;
  messageId?: string;
  interaction: LeadInteraction;
}

// Get leads with pagination and filtering
export async function getLeads(params: LeadQueryParams = {}): Promise<LeadListResult> {
  const { data } = await api.get<LeadListResult>('/leads', { params });
  return data;
}

// Get a single lead by ID with interactions
export async function getLead(id: string): Promise<Lead> {
  const { data } = await api.get<Lead>(`/leads/${id}`);
  return data;
}

// Create a new lead
export async function createLead(leadData: CreateLeadData): Promise<Lead> {
  const { data } = await api.post<Lead>('/leads', leadData);
  return data;
}

// Story 45.7/45.8 — cria card do pipeline a partir de paciente existente.
export interface CreateLeadFromPatientData {
  patientId: string;
  columnId?: string;
}

export async function createLeadFromPatient(payload: CreateLeadFromPatientData): Promise<Lead> {
  const { data } = await api.post<Lead>('/leads/from-patient', payload);
  return data;
}

// Update a lead
export async function updateLead(id: string, leadData: UpdateLeadData): Promise<Lead> {
  const { data } = await api.patch<Lead>(`/leads/${id}`, leadData);
  return data;
}

// Archive (soft delete) a lead
export async function archiveLead(id: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/leads/${id}`);
  return data;
}

// Restore an archived lead
export async function restoreLead(id: string): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/leads/${id}/restore`);
  return data;
}

// Move lead to different column
export async function moveLead(id: string, moveData: MoveLeadData): Promise<Lead> {
  const { data } = await api.patch<Lead>(`/leads/${id}/move`, moveData);
  return data;
}

// Convert lead to patient
export async function convertLead(id: string): Promise<{ lead: Lead; patient: { id: string; name: string } }> {
  const { data } = await api.post<{ lead: Lead; patient: { id: string; name: string } }>(`/leads/${id}/convert`);
  return data;
}

// Add interaction to lead
export async function addInteraction(leadId: string, interactionData: CreateInteractionData): Promise<LeadInteraction> {
  const { data } = await api.post<LeadInteraction>(`/leads/${leadId}/interactions`, interactionData);
  return data;
}

// Get kanban columns
export async function getKanbanColumns(): Promise<KanbanColumn[]> {
  const { data } = await api.get<KanbanColumn[]>('/kanban/columns');
  return data;
}

// Apply aesthetic / laser clinic preset
export async function applyAestheticLaserKanbanPreset(): Promise<KanbanColumn[]> {
  const { data } = await api.post<KanbanColumn[]>('/kanban/columns/apply-preset/aesthetic-laser');
  return data;
}

// Reorder columns
export async function reorderColumns(columnIds: string[]): Promise<KanbanColumn[]> {
  const { data } = await api.post<KanbanColumn[]>('/kanban/columns/reorder', { columnIds });
  return data;
}

// Create column
export interface CreateColumnData {
  name: string;
  color?: string;
  isDefault?: boolean;
}

export async function createColumn(columnData: CreateColumnData): Promise<KanbanColumn> {
  const { data } = await api.post<KanbanColumn>('/kanban/columns', columnData);
  return data;
}

// Update column
export interface UpdateColumnData {
  name?: string;
  color?: string;
  isDefault?: boolean;
}

export async function updateColumn(id: string, columnData: UpdateColumnData): Promise<KanbanColumn> {
  const { data } = await api.patch<KanbanColumn>(`/kanban/columns/${id}`, columnData);
  return data;
}

// Delete column
export async function deleteColumn(id: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/kanban/columns/${id}`);
  return data;
}

// Send WhatsApp message to lead
export async function sendMessage(leadId: string, messageData: SendMessageData): Promise<SendMessageResult> {
  const { data } = await api.post<SendMessageResult>(`/leads/${leadId}/send-message`, messageData);
  return data;
}
